import { type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useUnreadCount } from '@/hooks/useNotifications';
import { useMe } from '@/hooks/useUser';
import styles from './AppLayout.module.css';

interface Props {
  children: ReactNode;
}

export const AppLayout = ({ children }: Props) => {
  const location = useLocation();
  const { data: unreadCount } = useUnreadCount();
  const { data: me } = useMe();

  const isStaff = me?.role === 'admin' || me?.role === 'moderator';

  const hideTabBar =
    /^\/auction\//.test(location.pathname) ||
    /^\/user\//.test(location.pathname) ||
    location.pathname === '/create';

  return (
    <div className={styles.app}>
      <main className={clsx(styles.main, hideTabBar && styles.mainFull)}>
        {children}
      </main>

      {!hideTabBar && (
        <nav className={styles.tabBar}>
          <TabItem to="/" label="Лента" icon={<HomeIcon />} end />
          <TabItem to="/my" label="Мои" icon={<GavelIcon />} />
          <TabItem to="/create" label="Создать" icon={<PlusIcon />} primary />
          <TabItem
            to="/notifications"
            label="Уведом."
            icon={<BellIcon />}
            badge={unreadCount && unreadCount > 0 ? unreadCount : undefined}
          />
          <TabItem to="/profile" label="Профиль" icon={<UserIcon />} />
          {isStaff && <TabItem to="/admin" label="Админ" icon={<ShieldIcon />} />}
        </nav>
      )}
    </div>
  );
};

// ── Tab item ───────────────────────────────────────────
interface TabItemProps {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
  primary?: boolean;
  badge?: number;
}

const TabItem = ({ to, label, icon, end, primary, badge }: TabItemProps) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      clsx(
        styles.tabItem,
        isActive && styles.tabItemActive,
        primary && styles.tabItemPrimary,
      )
    }
  >
    <div className={styles.tabIconWrap}>
      {icon}
      {badge !== undefined && (
        <span className={styles.badge}>{badge > 99 ? '99+' : badge}</span>
      )}
    </div>
    <span className={styles.tabLabel}>{label}</span>
  </NavLink>
);

// ── Иконки ─────────────────────────────────────────────
function HomeIcon() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5L12 3l9 6.5V21H3V9.5z" /><path d="M9 21v-7h6v7" /></svg>; }
function GavelIcon() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 6l6 6" /><path d="M8 12l6-6" /><path d="M3 21l8-8" /><path d="M16 18h6" /></svg>; }
function PlusIcon() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>; }
function BellIcon() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>; }
function UserIcon() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" /></svg>; }
function ShieldIcon() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" /></svg>; }